import React from 'react';
import { useAuthContext } from '../../../context/AuthContext';

export type ForgeEnvironmentId = 'prod' | 'staging' | 'local';

type LocationLike = Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>;

interface ForgeEnvironmentLink {
  id: ForgeEnvironmentId;
  label: string;
  title: string;
  href: string;
  isActive: boolean;
}

const FORGE_ENVIRONMENTS: Array<{
  id: ForgeEnvironmentId;
  label: string;
  origin: string;
  title: string;
}> = [
  {
    id: 'prod',
    label: 'Prod',
    origin: 'https://control.focal.systems',
    title: 'Open this Forge page in production',
  },
  {
    id: 'staging',
    label: 'Stg',
    origin: 'https://control.staging.focal.dev',
    title: 'Open this Forge page in staging',
  },
  {
    id: 'local',
    label: 'Local',
    origin: 'http://localhost:3000',
    title: 'Open this Forge page locally',
  },
];

export const getCurrentForgeEnvironment = (origin: string): ForgeEnvironmentId | undefined => {
  if (origin === 'https://control.focal.systems') return 'prod';
  if (origin === 'https://control.staging.focal.dev') return 'staging';
  if (origin === 'http://localhost:3000' || origin === 'http://127.0.0.1:3000') {
    return 'local';
  }
  return undefined;
};

export const getCurrentForgeRoute = (location: LocationLike): string => {
  if (!location.pathname.startsWith('/forge')) {
    return '/forge';
  }

  return `${location.pathname}${location.search}${location.hash}`;
};

export const buildForgeEnvironmentLinks = (
  location: LocationLike = window.location
): ForgeEnvironmentLink[] => {
  const route = getCurrentForgeRoute(location);
  const activeEnvironment = getCurrentForgeEnvironment(location.origin);

  return FORGE_ENVIRONMENTS.map((environment) => ({
    id: environment.id,
    label: environment.label,
    title: environment.title,
    href: `${environment.origin}${route}`,
    isActive: environment.id === activeEnvironment,
  }));
};

export const ForgeEnvironmentSwitcher: React.FC = () => {
  const { hasInternalAccess } = useAuthContext();

  if (!hasInternalAccess) {
    return null;
  }

  const links = buildForgeEnvironmentLinks();

  return (
    <div className="forge-env-switcher" role="group" aria-label="Open Forge environment">
      {links.map((link) => (
        <a
          key={link.id}
          className={`forge-env-link ${link.isActive ? 'active' : ''}`}
          href={link.href}
          title={link.title}
          aria-current={link.isActive ? 'page' : undefined}
        >
          <span className="forge-env-label-full">{link.label}</span>
          <span className="forge-env-label-short" aria-hidden="true">
            {link.label[0]}
          </span>
        </a>
      ))}
    </div>
  );
};
